import type { MPoints, MPointsEpoch, RewardsConfig } from '@/api/spec.ts';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';
import { pointsAmountToDisplay } from '../../points/pointsCampaign';
import type { MPointsEpochRow } from '../types';

// mp/total_mpoints arrive as an Amount string scaled by 10^6 (== collateral decimals) — identical
// shape to Perpl Points, so reuse the same scale-and-floor-to-2-decimals helper.
export const mpointsAmountToDisplay = pointsAmountToDisplay;

export interface MPointsCampaignStats {
  currentEpoch: number;
  totalEpochs: number;
  campaignEndMs: number;
  currentEpochEndMs: number;
  // Unlocked season reward pool in USD (from mpoints_pool_amount; grows with trading activity, up to $1M).
  prizePoolUsd: number;
  poolSize: number;
  perEpochPoolSize: number;
  epochDurationMs: number;
  campaignStartMs: number;
  collateralDecimals: number;
}

export interface ProfileMPointsDerived {
  projectedMonUsd: number | null;
  totalSoFarPct: number | null;
  earnedLastEpochPct: number | null;
}

export const MPOINTS_PRIZE_TIERS_USD = [600_000, 700_000, 800_000, 900_000, 1_000_000] as const;

// Top tier — once the unlocked pool reaches this, the season reward is fully unlocked.
export const MPOINTS_MAX_PRIZE_USD = MPOINTS_PRIZE_TIERS_USD[MPOINTS_PRIZE_TIERS_USD.length - 1];

// Fixed per-epoch pool shown in the mPoints Distribution card; intentionally hardcoded in the UI, not sourced from backend config.
// The season total is sourced from config (mpoints_pool_size) via stats.poolSize instead.
export const MPOINTS_POOL_THIS_EPOCH = 50_000;

const MAKER_BOOST_EPOCH_MPOINTS = 20_000;

export interface MPointsEpochBoost {
  base: number;
  added: number;
  total: number;
}

export const getEpochBoost = (enabled: boolean | undefined): MPointsEpochBoost | null =>
  enabled
    ? {
        base: MPOINTS_POOL_THIS_EPOCH,
        added: MAKER_BOOST_EPOCH_MPOINTS,
        total: MPOINTS_POOL_THIS_EPOCH + MAKER_BOOST_EPOCH_MPOINTS,
      }
    : null;

export interface PrizeTierStatus {
  amount: number;
  locked: boolean;
  progress?: number;
}

export const computePrizeTierStatuses = (prizePoolUsd: number): PrizeTierStatus[] =>
  // Progress fill is intentionally omitted until the backend exposes real per-tier progress;
  // tiers render locked/unlocked only. TODO(api): populate `progress` from real data.
  MPOINTS_PRIZE_TIERS_USD.map((amount) => ({
    amount,
    locked: prizePoolUsd < amount,
  }));

export const computeCampaignStats = (
  rewards: RewardsConfig | undefined,
  collateralDecimals: number,
  nowMs: number = Date.now()
): MPointsCampaignStats => {
  const totalEpochs = rewards?.mpoints_num_epochs ?? 0;
  const startsAt = rewards?.mpoints_epoch_starts_at ?? 0;
  const durationMs = (rewards?.mpoints_epoch_duration_sec ?? 0) * 1000;
  const campaignEndMs = startsAt > 0 && durationMs > 0 && totalEpochs > 0 ? startsAt + totalEpochs * durationMs : 0;

  let currentEpoch = 0;
  if (startsAt > 0 && durationMs > 0) {
    const elapsed = nowMs - startsAt;
    if (elapsed >= 0) {
      currentEpoch = Math.min(totalEpochs, Math.floor(elapsed / durationMs) + 1);
    }
  }

  // Unlocked USD pool: mpoints_pool_amount is a collateral-native-scaled (USD) amount.
  const prizePoolUsd = rewards?.mpoints_pool_amount
    ? offsetNumberToNumber(Number(rewards.mpoints_pool_amount), collateralDecimals)
    : 0;

  const poolSize = rewards?.mpoints_pool_size ?? 0;
  const perEpochPoolSize = totalEpochs > 0 ? Math.floor(poolSize / totalEpochs) : 0;

  // Pre-season counts down to the campaign start; startsAt is 0 when config is missing.
  const currentEpochEndMs = currentEpoch > 0 ? startsAt + currentEpoch * durationMs : startsAt;

  return {
    currentEpoch,
    totalEpochs,
    campaignEndMs,
    currentEpochEndMs,
    prizePoolUsd,
    poolSize,
    perEpochPoolSize,
    epochDurationMs: durationMs,
    campaignStartMs: startsAt,
    collateralDecimals,
  };
};

export const mapMPointEpochToRow = (epoch: MPointsEpoch, stats: MPointsCampaignStats): MPointsEpochRow => {
  const startSec = Math.floor(epoch.sts / 1000);
  const endSec = startSec + epoch.dur - 1;
  const mPoints = epoch.mp != null ? mpointsAmountToDisplay(epoch.mp, stats.collateralDecimals) : null;
  // USD value of one mPoint = the epoch's share of the unlocked USD pool.
  const perMPointUsd = stats.poolSize > 0 ? stats.prizePoolUsd / stats.poolSize : 0;
  // The backend epoch type (MPointsEpoch) carries no name field; derive a display name from its index.
  const epochName = `Week ${epoch.e}`;

  return {
    epoch: epoch.e,
    epochName,
    startDate: startSec,
    endDate: endSec,
    weeklyRank: epoch.r || null,
    // weeklyPoints is the shared Points column; mPoints has no per-epoch Perpl-points source (`pp` was removed).
    weeklyPoints: null,
    mPoints,
    referralPoints: null,
    mPointsUsd: mPoints != null && perMPointUsd > 0 ? mPoints * perMPointUsd : undefined,
    isCurrent: epoch.e === stats.currentEpoch,
  };
};

// Synthesize a row for a week the backend omits — live if current, otherwise pending.
const buildSyntheticEpochRow = (stats: MPointsCampaignStats, epoch: number, isCurrent: boolean): MPointsEpochRow | null => {
  if (epoch <= 0 || stats.epochDurationMs <= 0 || stats.campaignStartMs <= 0) return null;
  const startMs = stats.campaignStartMs + (epoch - 1) * stats.epochDurationMs;
  const startSec = Math.floor(startMs / 1000);
  const endSec = startSec + Math.floor(stats.epochDurationMs / 1000) - 1;
  return {
    epoch,
    startDate: startSec,
    endDate: endSec,
    epochName: `Week ${epoch}`,
    weeklyRank: null,
    weeklyPoints: null,
    referralPoints: null,
    mPoints: null,
    isCurrent,
    isPending: !isCurrent,
  };
};

// Backfill every week from current down to 1, so weeks the backend omits (undistributed) still show as pending.
export const buildEpochRows = (data: MPoints | undefined, stats: MPointsCampaignStats): MPointsEpochRow[] => {
  const settledByEpoch = new Map<number, MPointsEpochRow>();
  for (const e of data?.d ?? []) {
    settledByEpoch.set(e.e, mapMPointEpochToRow(e, stats));
  }

  // Before the campaign starts there are no weeks to synthesize.
  if (stats.currentEpoch <= 0) return [...settledByEpoch.values()];

  const rows: MPointsEpochRow[] = [];
  for (let epoch = stats.currentEpoch; epoch >= 1; epoch--) {
    const settled = settledByEpoch.get(epoch);
    if (settled) {
      rows.push(settled);
      continue;
    }
    const synthetic = buildSyntheticEpochRow(stats, epoch, epoch === stats.currentEpoch);
    if (synthetic) rows.push(synthetic);
  }
  return rows;
};

export const deriveProfileMPoints = (data: MPoints | undefined, stats: MPointsCampaignStats): ProfileMPointsDerived => {
  const total = mpointsAmountToDisplay(data?.total_mpoints ?? '0', stats.collateralDecimals);
  const lastSettled = (data?.d ?? []).find((e) => e.e !== stats.currentEpoch && e.mp != null);
  const lastSettledMp = lastSettled ? mpointsAmountToDisplay(lastSettled.mp, stats.collateralDecimals) : null;

  // User's projected USD = their share of the mPoints pool × the unlocked USD pool (null when unknown → em dash).
  const projectedMonUsd = stats.poolSize > 0 && stats.prizePoolUsd > 0 ? (total / stats.poolSize) * stats.prizePoolUsd : null;
  const totalSoFarPct = stats.poolSize > 0 ? (total / stats.poolSize) * 100 : null;
  const earnedLastEpochPct =
    lastSettledMp != null && stats.perEpochPoolSize > 0 ? (lastSettledMp / stats.perEpochPoolSize) * 100 : null;

  return { projectedMonUsd, totalSoFarPct, earnedLastEpochPct };
};
